import { Cart } from '@app/data/cart-bff';
import { Cart as MonolithCart } from '@app/types/monolith/cart';
import { mapCouponType } from '@app/types/monolith/utils';
import logger from '@redbubble/next/logger';
import {
  ShippingAddress,
  StripeExpressCheckoutElementConfirmEvent,
  BillingDetails as StripeSDKBillingDetails,
} from '@stripe/stripe-js';
import getConfig from 'next/config';
import { addDDError } from '../../pages/cart/utils/datadog';
import { STRIPE_VERIFY_INTENT_PUBLIC_PATH } from './constants';
import {
  InternalStripePaymentMethodType,
  PageSourceType,
  RBChargeSourceType,
  RbPaymentMethodType,
  StripeCart,
} from './types';
import {
  AddressDetails as PayRequestAddressDetails,
  PayRequestBody,
} from './usePay';

type NullableAddressField =
  | Pick<PayRequestAddressDetails['address'], 'city' | 'postal_code' | 'state'>
  | Pick<StripeSDKBillingDetails['address'], 'city' | 'postal_code' | 'state'>;

type NullableAddressFieldProps = {
  city: string;
  state: string;
  postal_code: string;
};

/*
  Stripe does not enforce validation for some address fields in some countries.
    - e.g. "city" in Hong Kong
  Stripe also does not demand or show specific address fields for some countries.
    - e.g. "postcode" in Hong Kong or "state" in Zimbabwe
  The address models in the Monolith expect these fields, so we default them to
  non-blank in order to pass the validations.
*/
export function assignDefaultValueForNullableAddressFields(
  fields: NullableAddressFieldProps
): Pick<PayRequestAddressDetails['address'], 'city' | 'postal_code' | 'state'>;
export function assignDefaultValueForNullableAddressFields(
  fields: NullableAddressFieldProps
): Pick<StripeSDKBillingDetails['address'], 'city' | 'postal_code' | 'state'>;

export function assignDefaultValueForNullableAddressFields(
  fields: NullableAddressFieldProps
): NullableAddressField {
  return {
    city: fields.city || '-',
    state: fields.state || '-',
    postal_code: fields.postal_code || '-',
  };
}

export const formatShippingAddress = (
  shippingAddress: ShippingAddress,
  billingDetailsPhone?: string
): PayRequestAddressDetails => {
  // Incoming shipping address from within Payment Request

  // For context on the '-' defaults, refer to the address
  // block comment at the top of this file

  const { name, address } = shippingAddress;
  return {
    name,
    phone_number: billingDetailsPhone || '',
    address: {
      line1: address.line1!,
      line2: address.line2!,
      country_code: address.country!,
      ...assignDefaultValueForNullableAddressFields({
        city: address.city,
        state: address.state,
        postal_code: address.postal_code,
      }),
    },
  };
};

export const formatBillingAddress = (
  billingAddress: StripeSDKBillingDetails
): PayRequestAddressDetails => {
  // Incoming billing address from within Payment Method

  // For context on the '-' defaults, refer to the address
  // block comment at the top of this file

  return {
    phone_number: billingAddress.phone || '',
    name: billingAddress.name,
    address: {
      country_code: billingAddress.address.country,
      line1: billingAddress.address.line1,
      line2: billingAddress.address.line2,
      ...assignDefaultValueForNullableAddressFields({
        city: billingAddress.address.city,
        state: billingAddress.address.state,
        postal_code: billingAddress.address.postal_code,
      }),
    },
  };
};

export const isBillingComplete = (
  billingAddress?: StripeSDKBillingDetails
): billingAddress is StripeSDKBillingDetails => {
  if (!billingAddress) {
    return false;
  }

  const {
    name,
    address: { line1, country },
  } = billingAddress;

  return [name, line1, country].every((field) => !!field);
};

export const convertStripeResponseToPayRequestBody = (
  stripeObj: StripeExpressCheckoutElementConfirmEvent,
  paymentMethodType: RbPaymentMethodType,
  pageSource: PageSourceType,
  rbEmail: string | null
): PayRequestBody => {
  const { shippingAddress, billingDetails } = stripeObj;

  const formattedShippingAddress = formatShippingAddress(
    shippingAddress!,
    stripeObj.billingDetails?.phone
  );
  const formattedBillingAddress = isBillingComplete(billingDetails)
    ? formatBillingAddress(billingDetails)
    : formattedShippingAddress;

  return {
    page_source: pageSource,
    payment_method_type: paymentMethodType,
    order_details: {
      email: rbEmail || stripeObj.billingDetails?.email || '',
      shipping_details: formattedShippingAddress,
      billing_details: formattedBillingAddress,
    },
  };
};

export const getStripePublishableKey = ({ currencyCode }) => {
  const { publicRuntimeConfig } = getConfig();
  const key = publicRuntimeConfig.stripe.publishableKeys[currencyCode];

  if (!key) {
    throw new Error(`No Stripe key for the given currency ${currencyCode}`);
  }

  return key;
};

// NOTE (jj) 20240925:
// stripe requires monetary amounts to be sent as an integer multiple of the smallest "subunit"
// aka, if we wanted to send $1.23 AUD, we need to represent this value an integer in "cents", 123
// At the time of writing, all of our supported currencies can be converted to
// 100 cents/pence per euro/dollar/pound.
// As such, the implementation here leverages naive assumption
//
// https://docs.stripe.com/js/elements_object/create_without_intent#stripe_elements_no_intent-options-amount
export const getSubunitAmount = (amount: number) => {
  return Math.round(amount * 100);
};

export const removeKeyFromObject = (object: Object, deleteKey: string) => {
  if (object == null) return;

  delete object[deleteKey];

  Object.values(object).forEach((val) => {
    if (typeof val !== 'object') return;

    removeKeyFromObject(val, deleteKey);
  });
};

export class UnknownStripePaymentMethod extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'UnknownStripePayment';
  }
}

export const stripePaymentMethodToChargeSource: Record<
  InternalStripePaymentMethodType,
  RBChargeSourceType
> = {
  card: 'STRIPE',
  apple_pay: 'APPLE_PAY',
  google_pay: 'GOOGLE_PAY',
  klarna: 'klarna',
  afterpay_clearpay: 'afterpay',
};

export const getChargeSourceFromStripePaymentMethod = (
  paymentMethod: string
) => {
  const rbChargeSource = stripePaymentMethodToChargeSource[paymentMethod];

  if (!rbChargeSource) {
    const error = new UnknownStripePaymentMethod(
      `Unknown Stripe payment method '${paymentMethod}' was passed in to get the charge source`
    );
    logger.error(
      {
        err: error,
        context: 'getChargeSourceFromStripePaymentMethod',
        message: error.message,
      },
      error.message
    );
    addDDError(error);

    return paymentMethod;
  }

  return rbChargeSource;
};

export const createPaymentRedirectUrl = ({
  chargeSource,
  currency,
  includeReturnUrl,
}: {
  chargeSource: string;
  currency: string;
  includeReturnUrl?: boolean;
}) => {
  const redirectUrl = new URL(window.location.origin);

  redirectUrl.pathname = STRIPE_VERIFY_INTENT_PUBLIC_PATH;
  redirectUrl.searchParams.append('charge_source', chargeSource);
  redirectUrl.searchParams.append('currency_iso', currency);

  if (includeReturnUrl) {
    redirectUrl.searchParams.append('return_url', window.location.pathname);
  }

  return redirectUrl;
};

export const stripeToRBPaymentMethodDict: Record<
  InternalStripePaymentMethodType,
  RbPaymentMethodType
> = {
  card: 'card',
  apple_pay: 'card',
  google_pay: 'card',
  klarna: 'klarna',
  afterpay_clearpay: 'afterpay_clearpay',
};

export const getRBPaymentMethodFromStripePaymentMethod = (
  paymentMethod: string
) => {
  const rbPaymentMethod = stripeToRBPaymentMethodDict[paymentMethod];

  if (!rbPaymentMethod) {
    const error = new UnknownStripePaymentMethod(
      `Unknown Stripe payment method '${paymentMethod}' was passed in to get the RB payment method`
    );
    logger.error(
      {
        err: error,
        context: 'getRBPaymentMethodFromStripePaymentMethod',
        message: error.message,
      },
      error.message
    );
    addDDError(error);

    return paymentMethod;
  }

  return rbPaymentMethod;
};

export const mapGQLCartToStripeCart = (cart: Cart): StripeCart => {
  return {
    summary: {
      currency: cart.summary.total.currency,
      subtotal: cart.summary.subtotal.amount,
      total: cart.summary.total.amount,
      estimatedShipping: {
        preference: cart.summary.shipping.selected.method,

        standard: cart.summary.shipping.estimated.standard.price.amount,
        standardExTax: cart.summary.shipping.estimated.standard.amountExTax,

        express: cart.summary.shipping.estimated.express
          ? cart.summary.shipping.estimated.express.price.amount
          : null,
        expressExTax: cart.summary.shipping.estimated.express
          ? cart.summary.shipping.estimated.express.amountExTax
          : null,
      },
      tax: cart.summary.tax
        ? {
            amount: cart.summary.tax.amount,
            type: cart.summary.tax.type,
          }
        : null,
      showTaxSeparately: cart.summary.showTaxSeparately,
      lineItemsCount: cart.summary.lineItemsCount,
    },
    appliedCoupon: cart.appliedCoupon
      ? {
          code: cart.appliedCoupon.code,
          displayName: cart.appliedCoupon.displayName,
          discount: cart.appliedCoupon.discount.amount,
          type: mapCouponType(cart.appliedCoupon.type),
        }
      : null,
  };
};

export const mapMonolithCartToStripeCart = (cart: MonolithCart): StripeCart => {
  const { applied_coupon, currency, total_items_count } = cart;
  const {
    total,
    tax,
    show_tax_separately,
    estimated_shipping: {
      standard,
      standard_ex_tax,
      express,
      express_ex_tax,
      preference,
    },
    subtotal_ex_tax,
    subtotal,
  } = cart.summary;

  const subtotalAmount = show_tax_separately ? subtotal_ex_tax : subtotal || 0;

  return {
    summary: {
      currency,
      subtotal: subtotalAmount,
      total,
      estimatedShipping: {
        preference: preference === 'express' ? 'EXPRESS' : 'STANDARD',
        standard,
        standardExTax: standard_ex_tax,
        express,
        expressExTax: express_ex_tax,
      },
      tax: tax
        ? {
            amount: tax.amount,
            type: tax.type,
          }
        : null,
      showTaxSeparately: show_tax_separately,
      lineItemsCount: total_items_count,
    },
    appliedCoupon: applied_coupon
      ? {
          code: applied_coupon.code,
          displayName: applied_coupon.display_name,
          discount: Number(applied_coupon.sub_discount.amount),
          type: applied_coupon.type,
        }
      : null,
  };
};
